import { create } from "zustand";

/**
 * ESG Passport Registry Store (v3)
 * 
 * Manages ESG passport registry, passport levels, entity scoring,
 * and registry health monitoring.
 */
export interface ESGPassport {
  id: string;
  entityName: string;
  esgScore: number;
  region: string;
  lastAuditISO: string;
  passportLevel: "basic" | "enhanced" | "sovereign";
}

export interface ESGPassportRegistryStoreState {
  passports: ESGPassport[];
  registryHealth: number; // 0-100
  revokedCount: number;
  updateLatencyMS: number;

  // Actions
  issuePassport: (entityName: string, esgScore: number, region: string) => void;
  revokePassport: (id: string) => void;

  // Derived outputs
  getPassportLevel: (esgScore: number) => "basic" | "enhanced" | "sovereign";
  getRegistryHealth: () => number;
  getPassportLevelDistribution: () => { basic: number; enhanced: number; sovereign: number };

  // Export
  exportPassportRegistry: () => void;
}

export const useESGPassportRegistryStore = create<ESGPassportRegistryStoreState>((set, get) => {
  const determineLevel = (esgScore: number): "basic" | "enhanced" | "sovereign" => {
    if (esgScore < 40) return "basic";
    if (esgScore < 76) return "enhanced";
    return "sovereign";
  };

  const recomputeRegistryHealth = () => {
    const state = get();
    const health = 100 - state.revokedCount * 2 - (state.updateLatencyMS / 20);
    const registryHealth = Math.max(0, Math.min(100, health));
    set({ registryHealth });
  };

  return {
    passports: [],
    registryHealth: 100,
    revokedCount: 0,
    updateLatencyMS: 0,

    issuePassport: (entityName, esgScore, region) => {
      const state = get();
      if (!entityName || esgScore === 0) return;

      const passportLevel = determineLevel(esgScore);
      const newPassport: ESGPassport = {
        id: `PASSPORT_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        entityName,
        esgScore,
        region,
        lastAuditISO: new Date().toISOString(),
        passportLevel,
      };

      set({
        passports: [...state.passports, newPassport],
      });

      recomputeRegistryHealth();
    },

    revokePassport: (id) => {
      const state = get();
      const passport = state.passports.find((p) => p.id === id);
      if (!passport) return;

      set({
        passports: state.passports.filter((p) => p.id !== id),
        revokedCount: state.revokedCount + 1,
      });

      recomputeRegistryHealth();
    },

    getPassportLevel: (esgScore) => {
      return determineLevel(esgScore);
    },

    getRegistryHealth: () => {
      return get().registryHealth;
    },

    getPassportLevelDistribution: () => {
      const state = get();
      const distribution = {
        basic: 0,
        enhanced: 0,
        sovereign: 0,
      };

      state.passports.forEach((passport) => {
        distribution[passport.passportLevel]++;
      });

      return distribution;
    },

    exportPassportRegistry: () => {
      const state = get();
      const distribution = state.getPassportLevelDistribution();
      const report = {
        passports: state.passports,
        registryHealth: state.getRegistryHealth(),
        revokedCount: state.revokedCount,
        updateLatencyMS: state.updateLatencyMS,
        passportLevelDistribution: distribution,
      };

      const dateStr = new Date().toISOString().split("T")[0].replace(/-/g, "");
      const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `passportRegistry_${dateStr}.json`;
      a.click();
      URL.revokeObjectURL(url);
    },
  };
});











